import React, { useMemo, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import HighlightOffIcon from '@mui/icons-material/HighlightOff';
import { useShouldLogOut } from 'hooks';
import { AUTH_ERROR } from 'utils/constants/errors';
import { ICustomError } from 'models';
import { ResultModalIcon, IIconProps } from 'components/icons';
import { Button } from 'components/base';
import { ModalWrapper } from './ModalWrapper';
import { SignOutForm } from '../signout-form';

import './modal.styles.scss';

interface ResultModalProps {
	onClose: () => void;
	type?: 'success' | 'fail' | 'warning' | 'custom';
	text?: string | JSX.Element;
	customError?: ICustomError;
	icon?: React.ElementType<IIconProps>;
	wrapperClass?: string;
}

export const ResultModal = ({
	onClose,
	type = 'success',
	text = '',
	customError,
	icon: ResultIcon,
	wrapperClass,
}: ResultModalProps) => {
	const { t } = useTranslation('common');
	const { shouldLogOut, setShouldLogOut } = useShouldLogOut();

	const handleClick = () => {
		if (typeof text === 'string' && text.includes(AUTH_ERROR)) {
			setShouldLogOut(true);
		}
		onClose();
	};

	const Icon = useMemo(() => {
		if (type === 'fail' || type === 'warning') {
			return <HighlightOffIcon className="modal-icon-fail" />;
		}

		return ResultIcon ? (
			<ResultIcon iconClass="modal-icon-result" />
		) : (
			<ResultModalIcon iconClass="modal-icon-result" />
		);
	}, [type, ResultIcon]);

	const getTitle = useCallback(() => {
		switch (type) {
			case 'success':
				return t('modals.great');
			case 'fail':
				return t('modals.fail');
			case 'warning':
				return t('modals.warning');
			case 'custom':
				return customError?.title;
			default:
				return '';
		}
	}, [type, t, customError]);

	const displayedText =
		typeof text === 'string' && text.includes(AUTH_ERROR)
			? t('errors.auth_error')
			: text || customError?.problem;

	return (
		<ModalWrapper
			type="result"
			onClose={handleClick}
			contentWrapperClass={wrapperClass}
		>
			{Icon}
			<h2 className="modal-result__title">{getTitle()}</h2>
			<p className="modal-result__text">{displayedText}</p>
			{customError && (
				<p className="modal-result__text">{customError?.solution}</p>
			)}
			<Button
				buttonText="Ok"
				buttonClass="modal-result__btn"
				onClick={handleClick}
			/>
			<SignOutForm shouldLogOut={shouldLogOut} hide />
		</ModalWrapper>
	);
};
